import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useParams } from 'react-router-dom';

interface EventContextType {
  selectedEvent: string | null;
  isEventContext: boolean;
  setSelectedEvent: (eventSlug: string | null) => void;
  getEventUrl: (path: string) => string;
  getOrgUrl: (path: string) => string;
}

const EventContext = createContext<EventContextType | undefined>(undefined);

interface EventProviderProps {
  children: ReactNode;
}

export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    try {
      // Extract event slug from URL pattern: /organizations/:orgSlug/events/:eventSlug/*
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const eventsIndex = pathSegments.findIndex(segment => segment === 'events');

      if (eventsIndex !== -1 && pathSegments[eventsIndex + 1]) {
        const eventSlug = pathSegments[eventsIndex + 1];
        // Ensure we have a valid string and it's not an action route
        if (typeof eventSlug === 'string' && eventSlug && eventSlug !== 'create' && !eventSlug.includes('edit')) {
          setSelectedEvent(eventSlug);
        } else {
          setSelectedEvent(null);
        }
      } else {
        setSelectedEvent(null);
      }
    } catch (error) {
      // If there's any error in processing, set to null
      setSelectedEvent(null);
    }
  }, [location.pathname]);

  const isEventContext = selectedEvent !== null;

  const getEventUrl = (path: string): string => {
    try {
      const orgSlug = params.orgSlug;
      const eventSlug = selectedEvent;

      // Ensure we have strings, not objects
      if (!orgSlug || !eventSlug || typeof orgSlug !== 'string' || typeof eventSlug !== 'string') {
        return path || '';
      }

      // Remove leading slash if present
      const cleanPath = (path || '').startsWith('/') ? (path || '').slice(1) : (path || '');
      return `/organizations/${String(orgSlug)}/events/${String(eventSlug)}/${cleanPath}`;
    } catch (error) {
      return path || '';
    }
  };

  const getOrgUrl = (path: string): string => {
    try {
      const orgSlug = params.orgSlug;

      // Ensure we have a string, not an object
      if (!orgSlug || typeof orgSlug !== 'string') {
        return path || '';
      }

      // Remove leading slash if present
      const cleanPath = (path || '').startsWith('/') ? (path || '').slice(1) : (path || '');
      return `/organizations/${String(orgSlug)}/${cleanPath}`;
    } catch (error) {
      return path || '';
    }
  };

  const contextValue: EventContextType = {
    selectedEvent,
    isEventContext,
    setSelectedEvent,
    getEventUrl,
    getOrgUrl,
  };

  return (
    <EventContext.Provider value={contextValue}>
      {children}
    </EventContext.Provider>
  );
};

export const useEventContext = (): EventContextType => {
  const context = useContext(EventContext);
  if (context === undefined) {
    throw new Error('useEventContext must be used within an EventProvider');
  }
  return context;
};